import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/navbar";
import "../css/dashboard.css";
import "../css/interview.css";

const TOTAL_QUESTIONS = 6;
const FREE_QUESTION_LIMIT = 1;

export default function InterviewSimulator() {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [step, setStep] = useState("setup");

  // Interview State
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [qnaPairs, setQnaPairs] = useState([]);
  const [results, setResults] = useState(null);

  // Hub / Local Storage State
  const [pastSessions, setPastSessions] = useState([]);

  // TTS Voice State
  const [preferredVoice, setPreferredVoice] = useState(null);

  // Access & Paywall State
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [showProModal, setShowProModal] = useState(false);

  // Speech Recognition State
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  // 1. Load Local Sessions & Setup Voices
  useEffect(() => {
    const savedSessions = localStorage.getItem("interview_sessions");
    if (savedSessions) {
      try {
        setPastSessions(JSON.parse(savedSessions));
      } catch (e) {
        console.error("Failed to parse local sessions");
      }
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const bestVoice =
        voices.find(
          (v) =>
            v.lang.startsWith("en") &&
            (v.name.includes("Google") ||
              v.name.includes("Premium") ||
              v.name.includes("Natural")),
        ) ||
        voices.find((v) => v.lang === "en-GB" || v.lang === "en-US") ||
        voices[0];

      setPreferredVoice(bestVoice);
    };

    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // 2. Initial Auth & Data Fetch (Allows Free Users in for 1 Free Question)
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const authRes = await fetch(`${API_URL}/api/user/status`, {
          credentials: "include",
        });
        if (!authRes.ok) throw new Error("Unauthorized");
        const userData = await authRes.json();

        setIsPro(Boolean(userData.isPro));

        const resRes = await fetch(`${API_URL}/api/resumes/list`, {
          credentials: "include",
        });
        const resData = await resRes.json();
        setResumes(resData.resumes || []);
        if (resData.resumes?.length > 0) {
          setSelectedResume(resData.resumes[0].id);
        }

        setIsCheckingAccess(false);
      } catch (err) {
        navigate("/auth");
      }
    };
    checkAccess();

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setCurrentAnswer(transcript);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, [API_URL, navigate]);

  // 3. Start the Interview (Requests 6 Questions) - UPDATED WITH AIRTIGHT ERROR HANDLING
  const handleStart = async () => {
    if (!selectedResume || !jobDescription.trim()) {
      toast.error("Please select a CV and paste a job description.");
      return;
    }

    setStep("loading");
    try {
      const res = await fetch(`${API_URL}/api/interview/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resumeId: selectedResume,
          jobDescription,
          questionCount: TOTAL_QUESTIONS,
        }),
      });

      // ERROR CATCH 1: Server crashes (e.g., Heroku/Nginx timeouts) return HTML, not JSON.
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server error: Received an invalid response.");
      }

      const data = await res.json();

      // ERROR CATCH 2: Pass the EXACT backend message to the catch block
      if (!res.ok) {
        throw new Error(
          data.message || `Error ${res.status}: Failed to generate questions.`,
        );
      }

      if (Array.isArray(data.questions) && data.questions.length > 0) {
        const sessionQuestions = data.questions.slice(0, TOTAL_QUESTIONS);
        setQuestions(sessionQuestions);
        setCurrentIndex(0);
        setQnaPairs([]);
        setCurrentAnswer("");
        setStep("active");
        readQuestionAloud(sessionQuestions[0]);
      } else {
        throw new Error(
          "The AI failed to generate valid questions. Please try again.",
        );
      }
    } catch (err) {
      // Displays the specific backend error, or a fallback for network disconnections
      toast.error(
        err.message || "Network error. Please check your connection.",
      );
      setStep("setup");
    }
  };

  // 4. Enhanced TTS playback
  const readQuestionAloud = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      utterance.rate = 0.95;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error(
        "Speech recognition is not supported in this browser. Please type your answer.",
      );
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  // 5. Submit Answer & Grade Session - UPDATED WITH 403 TRAP & ROLLBACK
  const submitAnswer = async () => {
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
    window.speechSynthesis.cancel();

    const newPair = {
      question: questions[currentIndex],
      answer: currentAnswer,
    };
    const updatedPairs = [...qnaPairs, newPair];
    setQnaPairs(updatedPairs);
    setCurrentAnswer("");

    // Free users only get Question 1 (index 0); Pro users get all 6 questions
    const maxAllowedQuestions = isPro
      ? questions.length
      : Math.min(FREE_QUESTION_LIMIT, questions.length);

    if (currentIndex < maxAllowedQuestions - 1) {
      setCurrentIndex((prev) => prev + 1);
      readQuestionAloud(questions[currentIndex + 1]);
    } else {
      setStep("grading");
      try {
        const res = await fetch(`${API_URL}/api/interview/grade`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ qnaPairs: updatedPairs, jobDescription }),
        });

        // ERROR CATCH 1: Prevent JSON parse crashes on 502/503 server errors
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error(
            "Server error: Received an invalid response while grading.",
          );
        }

        const data = await res.json();

        // ERROR CATCH 2: Handle specific HTTP status codes & exploits
        if (!res.ok) {
          if (res.status === 403) {
            // Exploit intercepted: visibily pop the paywall modal
            setShowProModal(true);
          }
          throw new Error(
            data.message || `Error ${res.status}: Failed to grade interview.`,
          );
        }

        // Success Path
        const enrichedResults = {
          ...data,
          isFreeTrialSample: !isPro,
          lockedQuestions: !isPro ? questions.slice(FREE_QUESTION_LIMIT) : [],
        };
        setResults(enrichedResults);

        // SAVE TO LOCAL STORAGE HUB (Max 50 sessions)
        const newSession = {
          id: Date.now(),
          date: new Date().toISOString(),
          resumeId: selectedResume,
          results: enrichedResults,
        };
        const updatedSessions = [newSession, ...pastSessions].slice(0, 50);
        setPastSessions(updatedSessions);
        localStorage.setItem(
          "interview_sessions",
          JSON.stringify(updatedSessions),
        );

        setStep("results");
      } catch (err) {
        // ERROR CATCH 3: Dynamic error toasts
        toast.error(
          err.message || "Network error while grading. Please try again.",
        );

        // ERROR CATCH 4: Graceful UI Recovery (Rollback so user doesn't lose their typed answer)
        setStep("active");
        const revertedPairs = [...updatedPairs];
        const lastPair = revertedPairs.pop();
        setQnaPairs(revertedPairs);
        setCurrentAnswer(lastPair.answer);
      }
    }
  };

  // 6. Hub Management Methods
  const viewPastSession = (session) => {
    setResults(session.results);
    setStep("results");
  };

  const handleDeleteSession = (id, e) => {
    e.stopPropagation();
    const updatedSessions = pastSessions.filter((session) => session.id !== id);
    setPastSessions(updatedSessions);
    localStorage.setItem("interview_sessions", JSON.stringify(updatedSessions));
    toast.success("Session removed");
  };

  const resetToHub = () => {
    setStep("setup");
    setCurrentIndex(0);
    setCurrentAnswer("");
    setQnaPairs([]);
    setQuestions([]);
    setResults(null);
  };

  if (isCheckingAccess) {
    return (
      <div className="dashboard-layout">
        <Navbar />
        <div
          className="sim-loading center-flex fade-in"
          style={{ width: "100%" }}
        >
          <div className="spinner"></div>
          <h2>Verifying Access...</h2>
        </div>
      </div>
    );
  }

  const totalSessionQuestions = questions.length || TOTAL_QUESTIONS;

  return (
    <>
      <Helmet>
        <title>Resumify | Interview Simulator</title>
        <meta
          name="description"
          content="Practise for your next job interview with Resumify's Interview Simulator. Try 1 question free or unlock all 6 tailored questions with Pro."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="dashboard-layout">
        <Navbar />
        <Toaster position="bottom-right" />

        {/* PRO SUBSCRIPTION POPUP MODAL */}
        {showProModal && (
          <div className="custom-modal-overlay" style={{ zIndex: 1000 }}>
            <div className="custom-modal-content">
              <div
                className="modal-icon-danger"
                style={{ fontSize: "3rem", marginBottom: "10px" }}
              >
                💎
              </div>
              <h3>Unlock All 6 Interview Questions</h3>
              <p>
                Free accounts include <strong>1 free practice question</strong>{" "}
                per session so you can test the AI interviewer and grading.
                Upgrade to Pro to unlock all{" "}
                <strong>6 tailored questions</strong> per session and unlimited
                practice.
              </p>
              <div
                className="custom-modal-actions"
                style={{ flexDirection: "column", gap: "10px" }}
              >
                <button
                  className="modal-btn"
                  style={{ background: "#4f46e5", color: "white" }}
                  onClick={() => navigate("/pricing")}
                >
                  Upgrade to Pro
                </button>
                <button
                  className="modal-btn cancel"
                  onClick={() => setShowProModal(false)}
                >
                  Continue with Free Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MAIN CONTENT AREA */}
        <main className="dashboard-main simulator-main">
          {step === "setup" && (
            <div className="sim-setup-container fade-in">
              <div className="main-header" style={{ marginBottom: "20px" }}>
                <div>
                  <h1>Interview Simulator</h1>
                  <p>
                    Practise tailored interview questions and get instant STAR
                    feedback.
                  </p>
                </div>
                {isPro ? (
                  <span className="pro-badge">
                    💎 Pro Active • 6 Questions / Session
                  </span>
                ) : (
                  <span
                    className="pro-badge"
                    style={{
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                    }}
                  >
                    🎁 Free Trial • 1 of 6 Questions Free
                  </span>
                )}
              </div>

              {/* Explicit Plan Allowance Banner */}
              <div
                style={{
                  background: isPro ? "#f0fdf4" : "#fffbeb",
                  border: `1px solid ${isPro ? "#bbf7d0" : "#fde68a"}`,
                  color: isPro ? "#166534" : "#92400e",
                  padding: "14px 18px",
                  borderRadius: "10px",
                  marginBottom: "20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                  fontSize: "0.92rem",
                  lineHeight: "1.5",
                }}
              >
                <div>
                  {isPro ? (
                    <>
                      <strong>💎 Full 6-Question Pro Mode:</strong> Each session
                      generates <strong>6 tailored interview questions</strong>{" "}
                      with full AI grading and keyword analysis.
                    </>
                  ) : (
                    <>
                      <strong>ℹ️ Free Preview (1 of 6 Questions):</strong> A
                      full interview consists of <strong>6 questions</strong>.
                      On the free plan, you will answer{" "}
                      <strong>Question 1 of 6 for free</strong> and receive a
                      full AI score and critique. Questions 2–6 require a Pro
                      upgrade.
                    </>
                  )}
                </div>
                {!isPro && (
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    style={{
                      background: "#d97706",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 14px",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Unlock All 6 Questions →
                  </button>
                )}
              </div>

              {/* Start New Session Card */}
              <div className="stat-card sim-card">
                <h3 style={{ marginBottom: "16px", color: "#1e293b" }}>
                  Start New Session
                </h3>
                <div className="form-group">
                  <label className="stat-label">1. Select Target CV</label>
                  <select
                    className="sim-input"
                    value={selectedResume}
                    onChange={(e) => setSelectedResume(e.target.value)}
                  >
                    <option value="" disabled>
                      Select a saved CV
                    </option>
                    {resumes.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.title} (
                        {new Date(r.last_updated).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginTop: "24px" }}>
                  <label className="stat-label">2. Paste Job Description</label>
                  <textarea
                    className="sim-input sim-textarea"
                    rows="6"
                    placeholder="Paste the job requirements here so we can tailor your 6 interview questions..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                  />
                </div>

                <button
                  className="sim-btn primary"
                  onClick={handleStart}
                  style={{ marginTop: "24px", width: "100%" }}
                >
                  {isPro
                    ? "Start 6-Question Interview Session"
                    : "Start Free Trial (Try Question 1 of 6 Free)"}
                </button>
              </div>

              {/* Local Storage Session Hub */}
              <div className="stat-card sim-card" style={{ marginTop: "30px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <h3 style={{ margin: 0, color: "#1e293b" }}>
                    Your Past Sessions
                  </h3>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    {pastSessions.length}/50 locally saved
                  </span>
                </div>

                {pastSessions.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {pastSessions.map((session) => (
                      <div
                        key={session.id}
                        onClick={() => viewPastSession(session)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.borderColor = "#cbd5e1")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.borderColor = "#e2e8f0")
                        }
                      >
                        <div>
                          <strong
                            style={{ display: "block", color: "#334155" }}
                          >
                            {new Date(session.date).toLocaleDateString()} at{" "}
                            {new Date(session.date).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </strong>
                          <span
                            style={{ fontSize: "0.8rem", color: "#64748b" }}
                          >
                            {session.results?.isFreeTrialSample
                              ? "Free Trial (1 of 6 Questions Graded)"
                              : "Full Pro Session (6 of 6 Questions Graded)"}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                          }}
                        >
                          <div
                            className={`ats-score-badge ${
                              session.results.overallScore >= 80
                                ? "score-high"
                                : session.results.overallScore >= 50
                                  ? "score-med"
                                  : "score-low"
                            }`}
                          >
                            <span className="score-val">
                              {session.results.overallScore}%
                            </span>
                          </div>
                          <button
                            className="icon-btn"
                            title="Delete Session"
                            onClick={(e) => handleDeleteSession(session.id, e)}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "1.1rem",
                              padding: "4px",
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: "0.95rem",
                      margin: 0,
                    }}
                  >
                    No past sessions found. Complete an interview to see your
                    history here.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === "loading" && (
            <div className="sim-loading center-flex fade-in">
              <div className="spinner"></div>
              <h2>Generating 6 Tailored Questions...</h2>
              <p>Analysing your CV against the job description.</p>
            </div>
          )}

          {step === "grading" && (
            <div className="sim-loading center-flex fade-in">
              <div className="spinner"></div>
              <h2>
                {isPro
                  ? "Analysing All 6 Answers..."
                  : "Analysing Your Free Trial Answer (Question 1 of 6)..."}
              </h2>
              <p>Evaluating STAR method usage and keyword matching.</p>
            </div>
          )}

          {step === "active" && (
            <div className="sim-active-container fade-in">
              {/* Visual 6-Question Pill Tracker */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "14px",
                  flexWrap: "wrap",
                }}
              >
                {Array.from({ length: totalSessionQuestions }).map((_, idx) => {
                  const isLocked = !isPro && idx >= FREE_QUESTION_LIMIT;
                  const isCurrent = idx === currentIndex;
                  const isCompleted = idx < currentIndex;

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isLocked) setShowProModal(true);
                      }}
                      style={{
                        flex: 1,
                        minWidth: "95px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        textAlign: "center",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: isLocked ? "pointer" : "default",
                        background: isCurrent
                          ? "#2563eb"
                          : isCompleted
                            ? "#dcfce7"
                            : isLocked
                              ? "#f1f5f9"
                              : "#e2e8f0",
                        color: isCurrent
                          ? "#ffffff"
                          : isCompleted
                            ? "#166534"
                            : isLocked
                              ? "#64748b"
                              : "#334155",
                        border: isCurrent
                          ? "1px solid #1d4ed8"
                          : "1px solid #cbd5e1",
                      }}
                      title={
                        isLocked
                          ? "Locked on Free Plan — Click to upgrade to Pro"
                          : `Question ${idx + 1}`
                      }
                    >
                      {isLocked
                        ? `🔒 Q${idx + 1} (Pro)`
                        : idx === 0 && !isPro
                          ? `🎁 Q1 (Free)`
                          : `Q${idx + 1}`}
                    </div>
                  );
                })}
              </div>

              <div className="sim-progress-bar">
                <div
                  className="sim-progress-fill"
                  style={{
                    width: `${
                      ((currentIndex + 1) / totalSessionQuestions) * 100
                    }%`,
                  }}
                ></div>
              </div>

              <div className="stat-card sim-question-card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <span className="question-counter" style={{ margin: 0 }}>
                    Question {currentIndex + 1} of {totalSessionQuestions}
                  </span>

                  {!isPro && (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontWeight: "600",
                      }}
                    >
                      🎁 Free Trial Question (1 of 6) • Questions 2–6 require
                      Pro
                    </span>
                  )}
                </div>

                <h2 className="sim-question-text">{questions[currentIndex]}</h2>

                <div className="answer-area">
                  <textarea
                    className="sim-input sim-textarea large"
                    placeholder="Type your answer here, or click the microphone to speak..."
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                  />

                  <div className="sim-controls">
                    <button
                      className={`mic-btn ${isRecording ? "recording" : ""}`}
                      onClick={toggleRecording}
                      title="Toggle Microphone"
                    >
                      {isRecording
                        ? "🔴 Recording (Click to Stop)"
                        : "🎙️ Speak Answer"}
                    </button>

                    <button
                      className="sim-btn primary"
                      onClick={submitAnswer}
                      disabled={!currentAnswer.trim()}
                    >
                      {!isPro
                        ? "Submit Free Question (1 of 6) & View Feedback"
                        : currentIndex === totalSessionQuestions - 1
                          ? "Submit All 6 Answers & Finish"
                          : `Next Question (${currentIndex + 2} of ${totalSessionQuestions})`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === "results" && results && (
            <div className="sim-results-container fade-in">
              <div className="main-header">
                <div>
                  <h1>Interview Feedback</h1>
                  <p style={{ margin: "4px 0 0 0", color: "#64748b" }}>
                    {results.isFreeTrialSample
                      ? "Showing feedback for Question 1 of 6 (Free Trial)"
                      : "Showing full feedback for all 6 questions"}
                  </p>
                </div>
                <div className="overall-score">
                  Overall Score: <strong>{results.overallScore}/100</strong>
                </div>
              </div>

              <div className="stat-card summary-card">
                <h3>General Assessment</h3>
                <p>{results.summary}</p>
              </div>

              <div className="feedback-grid">
                {(results.evaluations || []).map((evalItem, idx) => (
                  <div key={idx} className="resume-card feedback-card">
                    <div className="card-header">
                      <h3>
                        Q{idx + 1} of {TOTAL_QUESTIONS}: {evalItem.question}
                      </h3>
                      <span
                        className={`role-badge ${
                          evalItem.score >= 8 ? "good" : "average"
                        }`}
                      >
                        Score: {evalItem.score}/10
                      </span>
                    </div>
                    <div
                      className="card-body"
                      style={{ flexDirection: "column" }}
                    >
                      <div className="feedback-section">
                        <strong>Critique:</strong>
                        <p>{evalItem.feedback}</p>
                      </div>

                      {evalItem.missedKeywords?.length > 0 && (
                        <div className="feedback-section">
                          <strong>Missed Keywords:</strong>
                          <div className="keyword-pills">
                            {evalItem.missedKeywords.map((kw) => (
                              <span key={kw} className="pill">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="feedback-section better-answer">
                        <strong>💡 Ideal Approach:</strong>
                        <p>{evalItem.betterAnswer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Locked Questions 2-6 Upsell Card for Free Trial Users */}
              {!isPro && results.lockedQuestions?.length > 0 && (
                <div
                  className="stat-card"
                  style={{
                    marginTop: "24px",
                    background: "#f8fafc",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginBottom: "14px",
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0, color: "#0f172a" }}>
                        🔒 Remaining 5 Tailored Questions (Questions 2–6)
                      </h3>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "0.9rem",
                          color: "#475569",
                        }}
                      >
                        You completed your 1 free question. Upgrade to Pro to
                        answer and get AI grading on the remaining 5 questions
                        from this session:
                      </p>
                    </div>
                    <button
                      className="sim-btn primary"
                      style={{ background: "#4f46e5" }}
                      onClick={() => navigate("/pricing")}
                    >
                      💎 Upgrade to Pro to Unlock All 6
                    </button>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {results.lockedQuestions.map((qText, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "12px 14px",
                          background: "#ffffff",
                          border: "1px dashed #cbd5e1",
                          borderRadius: "8px",
                          color: "#64748b",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span>
                          <strong>
                            Q{i + 2} of {TOTAL_QUESTIONS}:
                          </strong>{" "}
                          {qText}
                        </span>
                        <span
                          style={{
                            fontSize: "0.78rem",
                            fontWeight: "600",
                            background: "#f1f5f9",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          🔒 Pro Only
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "16px", marginTop: "30px" }}>
                <button
                  className="sim-btn primary"
                  onClick={resetToHub}
                  style={{ flex: 1 }}
                >
                  Back to Hub
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
